import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { CopyTradePortfolio } from '@flipster/apis/v1/copy-trade/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import { dialog } from '@flipster/react/design-system/dialog/layout';
import { IconButton } from '@flipster/react/design-system/icon-button';
import Formatted from '@flipster/react/design-utils/formatted';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import UserIcon from '@flipster/react/icon/generated/UserIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { divider, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { isNullable } from '@flipster/utils/nullable';
import Num from '@flipster/utils/num';

import { SymbolDisplay } from '../../../misc/display';
import { useShowAppDownloadAlert } from '../../../shared/app-download-alert';
import { useAuth } from '../../../shared/auth/state';
import { KycDialog } from '../../../shared/kyc/dialog';
import { SimpleLinePulseChart } from '../../../shared/simple-line-pulse-chart';
import { copyTradingHomeSegmentBunja } from '../../event';
import { CopyTradingSettingsModal } from '../../modal/copy-trading-settings';
import { InvitationCodeModal } from '../../modal/invitation-code';
import { ProfileIcon } from '../../shared/profile-icon';
import { meetMasterTraderBunja } from './state';
import { getPortfolioPerformance } from './utils';

interface MasterTraderCardProps {
  portfolio: CopyTradePortfolio;
  color?: 'light' | 'dark';
}
export const MasterTraderCard = ({ portfolio, color = 'light' }: MasterTraderCardProps) => {
  const overlay = useOverlay();
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const openAppDownloadAlert = useShowAppDownloadAlert();
  const { trackCopyTradingCopyButtonClick } = useBunja(copyTradingHomeSegmentBunja);
  const { periodAtom } = useBunja(meetMasterTraderBunja);
  const period = useAtomValue(periodAtom);
  const { isCookieLoggedIn, isValidKyc } = useAuth();
  const {
    portfolioId,
    userProfileId,
    nickname,
    isOwner,
    profileIconUrl,
    currentCopierCount,
    maxCopierCount,
    availabilityStatus,
    preferredSymbols,
    preferredLeverage,
    hasAccess,
  } = portfolio;
  const performance = getPortfolioPerformance(portfolio, period);
  const roiPct = performance?.roiPct ?? null;
  const pnlUsdt = performance?.pnlUsdt ?? null;
  const winRatePct = performance?.winRatePct ?? null;
  const highestPnlUsdt = performance?.highestPnlUsdt ?? null;
  const turnoverRatePct = performance?.turnoverRatePct ?? null;
  const pnlHistory = performance?.pnlHistory ?? [];
  const isPrivate = hasAccess === false;
  const copierLimitFull = currentCopierCount >= maxCopierCount;
  type Weather = 'neutral' | 'positive' | 'negative';
  const weather: Weather = isNullable(roiPct)
    ? 'neutral'
    : Num(roiPct).eq(0)
      ? 'neutral'
      : Num(roiPct).gt(0)
        ? 'positive'
        : 'negative';
  const openCopySettingsModal = () => {
    overlay.open(({ isOpen, close, unmount }) => (
      <CopyTradingSettingsModal isOpen={isOpen} onClose={close} onExit={unmount} id={portfolioId} />
    ));
  };
  return (
    <IsomorphicLink
      href={!isCookieLoggedIn ? '/login' : `/user/profile/${userProfileId}`}
      className={stack({
        gap: 16,
        py: 24,
        px: 16,
        rounded: 8,
        bg: color === 'light' ? 'background.default.level-1' : 'background.default.level-0',
        borderColor: 'border.default.level-2',
        borderWidth: color === 'light' ? 0 : 1,
        borderStyle: 'solid',
      })}
    >
      <div className={hstack({ gap: 16 })}>
        <div className={css({ pos: 'relative' })}>
          <ProfileIcon profileIconUrl={profileIconUrl} size={80} />
          {isOwner && (
            <Badge
              css={{
                textStyle: 'Text/M/Strong',
                color: 'content.accent.VIP',
                bg: 'background.default.level-3',
                borderColor: 'border.default.level-2',
                pos: 'absolute',
                left: '50%',
                bottom: 0,
                transform: 'translate(-50%, 50%)',
              }}
            >
              <FormattedMessage id='Copy.r251107.Me' />
            </Badge>
          )}
        </div>
        <div className={stack({ gap: 4, flex: 1 })}>
          <div className={hstack({ justify: 'space-between', gap: 8 })}>
            <p className={css({ textStyle: 'Title/S', color: 'content.default.level-5' })}>{nickname}</p>
            <IconButton hierarchy='quaternary' size='medium' icon={<ChevronRightIcon />} />
          </div>
          <div className={hstack()}>
            <UserIcon className={css({ p: 4 })} color={token('colors.content.default.level-5')} />
            <p className={css({ textStyle: 'Text/M/Monospace/Regular' })}>
              <span
                className={css({
                  color: copierLimitFull ? 'content.state.negative' : 'content.default.level-5',
                })}
              >
                {currentCopierCount}
              </span>
              <span className={css({ color: 'content.default.level-1' })}>/{maxCopierCount}</span>
            </p>
          </div>
          <div className={stack({ gap: 4, textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
            <div className={hstack({ justify: 'space-between', gap: 36 })}>
              <p>
                <FormattedMessage id='Copy.r251107.Preferred_symbols' />
              </p>
              <div className={hstack({ gap: 4 })}>
                {preferredSymbols.length < 1 && <span>-</span>}
                {preferredSymbols.map((symbol) => (
                  <SymbolDisplay key={symbol} symbol={symbol} icon={<SymbolDisplay.Icon size={20} space={0} />} />
                ))}
              </div>
            </div>
            <div className={hstack({ justify: 'space-between', gap: 45 })}>
              <p>
                <FormattedMessage id='Copy.r251107.Preferred_leverage' />
              </p>
              <p className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
                {preferredLeverage}x
              </p>
            </div>
          </div>
        </div>
      </div>
      <hr className={divider({ orientation: 'horizontal', color: 'border.default.level-1' })} />
      <div className={stack({ gap: 24 })}>
        <div className={stack({ gap: 6 })}>
          <div className={hstack({ justify: 'space-between' })}>
            <div className={stack({ gap: 4 })}>
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                <FormattedMessage id='Common.r251127.Roi' />
              </p>
              {isNullable(roiPct) ? (
                <span className={css({ textStyle: 'Title/L', color: 'content.default.level-3' })}>--</span>
              ) : (
                <Formatted.Percent
                  value={roiPct}
                  precision={2}
                  toFixed
                  withPlus
                  withColor
                  css={{ textStyle: 'Title/L', color: 'content.default.level-3' }}
                />
              )}
            </div>
            {pnlHistory.length > 0 && (
              <SimpleLinePulseChart
                type='mini'
                width={80}
                height={50}
                data={pnlHistory.map((history) => Number(history.pnlUsdt))}
                colors={
                  {
                    neutral: {
                      start: token('colors.border.default.level-4'),
                      end: token('colors.inverse.white'),
                    },
                    negative: {
                      start: token('colors.border.state.negative'),
                      end: token('colors.background.accent.sell'),
                    },
                    positive: {
                      start: token('colors.border.state.positive'),
                      end: token('colors.background.accent.buy'),
                    },
                  }[weather]
                }
              />
            )}
          </div>
          <div className={hstack({ gap: 4, textStyle: 'Text/M/Regular' })}>
            <p className={css({ color: 'content.default.level-3' })}>
              <FormattedMessage id='Autogen_common.r251126.s_PnL_USDT' />
            </p>
            {isNullable(pnlUsdt) ? (
              <span className={css({ color: 'content.default.level-3' })}>--</span>
            ) : (
              <Formatted.Number
                value={pnlUsdt}
                precision={UsdtPrecision}
                withPlus
                toFixed
                withColor
                css={{ color: 'content.default.level-3' }}
              />
            )}
          </div>
        </div>
        <div>
          <div className={stack({ gap: 16, textStyle: 'Text/M/Monospace/Regular' })}>
            <div className={hstack({ justify: 'space-between', gap: 24 })}>
              <p className={css({ color: 'content.default.level-3' })}>
                <FormattedMessage id='Copy.r251107.Win_rate' />
              </p>
              {isNullable(winRatePct) ? (
                <span className={css({ color: 'content.default.level-5' })}>--</span>
              ) : (
                <Formatted.Percent
                  value={winRatePct}
                  precision={2}
                  toFixed
                  css={{ color: 'content.default.level-5' }}
                />
              )}
            </div>
            <div className={hstack({ justify: 'space-between', gap: 24 })}>
              <p className={css({ color: 'content.default.level-3' })}>
                <FormattedMessage id='Copy.r251107.Highest_pnl' />
              </p>
              {isNullable(highestPnlUsdt) ? (
                <span className={css({ color: 'content.default.level-5' })}>--</span>
              ) : (
                <Formatted.Number
                  value={highestPnlUsdt}
                  css={{ color: 'content.default.level-5' }}
                  suffix={UsdtCurrency}
                  precision={UsdtPrecision}
                  toFixed
                />
              )}
            </div>
            <div className={hstack({ justify: 'space-between', gap: 24 })}>
              <p className={css({ color: 'content.default.level-3' })}>
                <FormattedMessage id='Copy.r251107.Turnover_rate' />
              </p>
              {isNullable(turnoverRatePct) ? (
                <span className={css({ color: 'content.default.level-5' })}>--</span>
              ) : (
                <p className={css({ color: 'content.default.level-5' })}>
                  <Formatted.Number value={turnoverRatePct} precision={2} toFixed />x
                </p>
              )}
            </div>
          </div>
        </div>
        <Button
          hierarchy='primary'
          color='accent'
          size='large'
          label={
            {
              AVAILABLE: isPrivate ? (
                <span className={hstack({ gap: 4, justify: 'center' })}>
                  <LockClosedIcon size={16} />
                  <FormattedMessage id='Copy.r251107.Copy' />
                </span>
              ) : (
                <FormattedMessage id='Copy.r251107.Copy' />
              ),
              COPYING_IN_PROGRESS: <FormattedMessage id='Copy.r251107.Copying' />,
              FULL_BY_COPIER_COUNT: <FormattedMessage id='Copy.r251107.Full' />,
              FULL_BY_INVESTMENT_AMOUNT: <FormattedMessage id='Copy.r251107.Copy_amount_limit_full' />,
            }[availabilityStatus]
          }
          disabled={availabilityStatus != 'AVAILABLE' || isOwner}
          onClick={(e) => {
            e.preventDefault();
            trackCopyTradingCopyButtonClick(nickname);
            if (!isCookieLoggedIn) {
              router.push('/login');
              return;
            }
            if (isMobile) {
              openAppDownloadAlert();
              return;
            }
            if (!isValidKyc) {
              dialog.open(({ isOpen, promise }) => <KycDialog isOpen={isOpen} close={promise.resolve} />);
              return;
            }
            if (isPrivate) {
              overlay.open(({ isOpen, close, unmount }) => (
                <InvitationCodeModal
                  isOpen={isOpen}
                  onClose={close}
                  onExit={unmount}
                  portfolioId={portfolioId}
                  onSuccess={openCopySettingsModal}
                />
              ));
              return;
            }
            openCopySettingsModal();
          }}
        />
      </div>
    </IsomorphicLink>
  );
};
